import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers, updateUser } from '@/services/api';
import { listInvitations } from '@/services/invitationService';
import { USER_ROLE_LABELS } from '@/types';
import type { UserRole } from '@/types';
import InviteUserModal from './components/InviteUserModal';

const ALL_ROLES = Object.keys(USER_ROLE_LABELS) as UserRole[];

/** Inline multi-role picker — dropdown rendered via portal to escape overflow:hidden table containers. */
function RolePicker({
  userId,
  currentRoles,
  onSave,
  disabled,
}: {
  userId: string;
  currentRoles: UserRole[];
  onSave: (id: string, roles: UserRole[]) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<UserRole[]>([]);
  // Tracks the button's position so the portal dropdown can be placed correctly.
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);

  function handleOpen() {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: r.bottom + window.scrollY + 4,
        left: r.left + window.scrollX,
        width: Math.max(r.width, 200),
      });
    }
    setSelected([...currentRoles]);
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
  }

  // Close on outside click or scroll
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      const target = e.target as Node;
      // Allow clicks inside the portal dropdown (no ref available, check by class)
      const dropdown = document.getElementById(`role-picker-${userId}`);
      if (dropdown && dropdown.contains(target)) return;
      if (triggerRef.current && triggerRef.current.contains(target)) return;
      handleClose();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, userId]);

  function toggle(role: UserRole) {
    setSelected((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  function save() {
    if (selected.length === 0) return;
    onSave(userId, selected);
    handleClose();
  }

  const dropdown = open
    ? createPortal(
        <div
          id={`role-picker-${userId}`}
          style={{ position: 'absolute', top: dropdownPos.top, left: dropdownPos.left, minWidth: dropdownPos.width, zIndex: 9999 }}
          className="bg-white border border-brand-border rounded shadow-lg p-2"
        >
          <ul role="listbox" aria-multiselectable="true" className="space-y-0.5">
            {ALL_ROLES.map((role) => (
              <li key={role}>
                <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-brand-surface rounded px-2 py-1">
                  <input
                    type="checkbox"
                    checked={selected.includes(role)}
                    onChange={() => toggle(role)}
                    className="accent-brand-accent"
                    aria-label={USER_ROLE_LABELS[role]}
                  />
                  {USER_ROLE_LABELS[role]}
                </label>
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-2 mt-2 pt-2 border-t">
            <button type="button" onClick={handleClose} className="text-xs text-brand-text-secondary hover:underline">
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={selected.length === 0}
              className="text-xs px-2 py-1 bg-brand-accent text-brand-text-inverse rounded disabled:opacity-40"
            >
              Apply
            </button>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => (open ? handleClose() : handleOpen())}
        className="flex flex-wrap gap-1 border rounded px-2 py-1 text-sm min-w-[140px] text-left disabled:opacity-50"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {currentRoles.length > 0
          ? currentRoles.map((r) => (
              <span key={r} className="bg-brand-status-new-bg text-brand-accent text-xs px-1.5 py-0.5 rounded-full">
                {USER_ROLE_LABELS[r]}
              </span>
            ))
          : <span className="text-brand-text-secondary">—</span>}
      </button>
      {dropdown}
    </>
  );
}

export default function UserManagement() {
  const qc = useQueryClient();
  const [showInviteModal, setShowInviteModal] = useState(false);

  const { data: users, isLoading } = useQuery({ queryKey: ['users'], queryFn: getUsers });
  const { data: invitations } = useQuery({ queryKey: ['invitations'], queryFn: listInvitations });

  const deactivate = useMutation({
    mutationFn: (id: string) => updateUser(id, { isActive: false }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const changeRoles = useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: UserRole[] }) =>
      updateUser(id, { roles }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">User Management</h1>
        <button
          onClick={() => setShowInviteModal(true)}
          className="px-3 py-1.5 bg-brand-accent text-brand-text-inverse rounded text-sm"
        >
          + Invite User
        </button>
      </div>


      {isLoading && <p className="text-brand-text-secondary">Loading…</p>}
      <div className="bg-white rounded shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-surface border-b">
            <tr>
              {['Name', 'Email', 'Role', 'Active', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-medium text-brand-text-secondary">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-border">
            {users?.map(u => (
              <tr key={u.userId} className="hover:bg-brand-surface">
                <td className="px-4 py-3 font-medium">{u.displayName}</td>
                <td className="px-4 py-3 text-brand-text-secondary">{u.email}</td>
                <td className="px-4 py-3">
                  <RolePicker
                    userId={u.userId}
                    currentRoles={(u.roles ?? [u.role]) as UserRole[]}
                    onSave={(id, roles) => changeRoles.mutate({ id, roles })}
                    disabled={changeRoles.isPending}
                  />
                </td>
                <td className="px-4 py-3">
                  <span className={u.isActive ? 'text-brand-accent' : 'text-brand-text-secondary'}>
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {u.isActive && (
                    <button
                      onClick={() => deactivate.mutate(u.userId)}
                      className="text-red-500 hover:underline text-xs"
                    >Deactivate</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invitations section */}
      {invitations && invitations.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-3">Pending Invitations</h2>
          <div className="bg-white rounded shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-brand-surface border-b">
                <tr>
                  {['Email', 'Name', 'Role', 'Status', 'Expires'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium text-brand-text-secondary">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {invitations.map(inv => (
                  <tr key={inv.invitationId} className="hover:bg-brand-surface">
                    <td className="px-4 py-3">{inv.invitedEmail}</td>
                    <td className="px-4 py-3">{inv.firstName} {inv.lastName}</td>
                    <td className="px-4 py-3">
                      {(inv.roles ?? [inv.role]).join(', ')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={
                        inv.status === 'Accepted' ? 'text-brand-accent' :
                        inv.status === 'Expired' ? 'text-brand-text-secondary' :
                        'text-brand-priority-high'
                      }>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-brand-text-secondary">
                      {new Date(inv.tokenExpiresAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showInviteModal && <InviteUserModal onClose={() => setShowInviteModal(false)} />}
    </div>
  );
}
